const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";

// ─── Core Request Helpers ──────────────────────────────────────────

async function authRequest(endpoint, options = {}) {
  const headers = { "Content-Type": "application/json", ...options.headers };
  const config = { ...options, headers };
  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.detail || `Error ${response.status}`);
  }
  return data;
}

// Single in-flight refresh promise so concurrent 401s only trigger one refresh.
let _refreshInFlight = null;

async function tryRefresh() {
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = (async () => {
    const refresh = localStorage.getItem("refresh");
    if (!refresh) return false;
    try {
      const res = await fetch(`${API_BASE_URL}/api/token/refresh/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      if (data.access) localStorage.setItem("access", data.access);
      if (data.refresh) localStorage.setItem("refresh", data.refresh);
      return true;
    } catch {
      return false;
    } finally {
      // Release after a tick so other concurrent callers see the same result.
      setTimeout(() => { _refreshInFlight = null; }, 0);
    }
  })();
  return _refreshInFlight;
}

function redirectToLogin() {
  localStorage.removeItem("access");
  localStorage.removeItem("refresh");
  if (window.location.pathname !== "/login" && window.location.pathname !== "/") {
    window.location.href = "/login";
  }
}

async function request(endpoint, options = {}) {
  const send = async () => {
    const token = localStorage.getItem("access");
    const headers = { "Content-Type": "application/json", ...options.headers };
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });
  };
  let response = await send();
  if (response.status === 401) {
    const ok = await tryRefresh();
    if (ok) {
      response = await send();
    } else {
      redirectToLogin();
      throw new Error("სესია ვადაგასულია");
    }
  }
  if (!response.ok) {
    let data = {};
    try { data = await response.json(); } catch {}
    throw new Error(data.detail || data.error || `Error ${response.status}`);
  }
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.indexOf("application/json") !== -1) {
    return response.json();
  }
  return;
}

async function downloadRequest(endpoint, options = {}) {
  const token = localStorage.getItem("access");
  const headers = { "Content-Type": "application/json", ...options.headers };
  if (token) headers.Authorization = `Bearer ${token}`;
  const config = { ...options, headers };
  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
  if (response.headers.get("Content-Type")?.includes("application/json")) {
    const errorData = await response.json();
    throw new Error(errorData.detail || errorData.error || `Error ${response.status}`);
  }
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.blob();
}

// ─── Auth ──────────────────────────────────────────────────────────

export async function login(email, password) {
  const data = await authRequest("/api/token/", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  localStorage.setItem("access", data.access);
  localStorage.setItem("refresh", data.refresh);
  localStorage.setItem("department", JSON.stringify(data.department));
  if (data.company) {
    localStorage.setItem("company", JSON.stringify(data.company));
  }
  return data;
}

export function getCompanyConfig() {
  try {
    return JSON.parse(localStorage.getItem("company")) || null;
  } catch {
    return null;
  }
}

export async function refreshToken() {
  const refresh = localStorage.getItem("refresh");
  if (!refresh) throw new Error("No refresh token found.");
  const data = await authRequest("/api/token/refresh/", {
    method: "POST",
    body: JSON.stringify({ refresh }),
  });
  localStorage.setItem("access", data.access);
  return data;
}

// ─── Emails ────────────────────────────────────────────────────────

export const getEmails = () => request("/api/emails/");
export const getEmailDetails = (id) => request(`/api/emails/${id}/`);
export const getPendingEmails = () => request("/api/emails/pending_approval/");
export const getSentEmails = () => request("/api/emails/sent/");

export const generateReply = (id, choice) =>
  request(`/api/emails/${id}/generate_reply/`, {
    method: "POST",
    body: JSON.stringify({ choice }),
  });

export const sendReply = (id, replyContent, action, attachPdf = false) =>
  request(`/api/emails/${id}/send_reply/`, {
    method: "POST",
    body: JSON.stringify({ reply_content: replyContent, action, attach_pdf: attachPdf }),
  });

export const generatePdf = (id, replyContent) =>
  downloadRequest(`/api/emails/${id}/generate_pdf/`, {
    method: "POST",
    body: JSON.stringify({ reply_content: replyContent }),
  });

export const setApprovalStatus = (id, newStatus, { rejectionComment, editedContent } = {}) =>
  request(`/api/emails/${id}/set_approval_status/`, {
    method: "POST",
    body: JSON.stringify({
      approval_status: newStatus,
      ...(rejectionComment && { rejection_comment: rejectionComment }),
      ...(editedContent && { edited_content: editedContent }),
    }),
  });

export const toggleArchiveStatus = (id) =>
  request(`/api/emails/${id}/toggle_archive/`, { method: "POST" });

export const returnForReclassification = (id, comment) =>
  request(`/api/emails/${id}/return_for_reclassification/`, {
    method: "POST",
    body: JSON.stringify({ comment }),
  });

export const forwardEmail = (id, recipientEmail, comment) =>
  request(`/api/emails/${id}/forward_email/`, {
    method: "POST",
    body: JSON.stringify({ recipient_email: recipientEmail, comment }),
  });

export const reassignEmail = (id, departmentIds) =>
  request(`/api/emails/${id}/reassign/`, {
    method: "POST",
    body: JSON.stringify({ department_ids: departmentIds }),
  });

export const getAttachmentUrl = (emailId, filename) => {
  const token = localStorage.getItem("access");
  return `${API_BASE_URL}/api/emails/${emailId}/attachment/${encodeURIComponent(filename)}/?token=${token}`;
};

export const fetchAttachmentBlob = (emailId, filename) =>
  downloadRequest(`/api/emails/${emailId}/attachment/${encodeURIComponent(filename)}/`);

export async function uploadDocuments(files, subject) {
  const token = localStorage.getItem("access");
  const formData = new FormData();
  for (const file of files) formData.append("documents", file);
  if (subject) formData.append("subject", subject);
  const response = await fetch(`${API_BASE_URL}/api/emails/upload_document/`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail || data.error || `Error ${response.status}`);
  }
  return response.json();
}

// ─── Department Categories ─────────────────────────────────────────

export const getDepartmentCategories = () => request("/api/department-categories/");

export const createDepartmentCategory = (data) =>
  request("/api/department-categories/", {
    method: "POST",
    body: JSON.stringify(data),
  });

export const updateDepartmentCategory = (id, data) =>
  request(`/api/department-categories/${id}/`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const deleteDepartmentCategory = (id) =>
  request(`/api/department-categories/${id}/`, { method: "DELETE" });

// ─── Departments (Users) ───────────────────────────────────────────

export const getDepartments = () => request("/api/departments/");

export const createDepartment = (data) =>
  request("/api/departments/", {
    method: "POST",
    body: JSON.stringify(data),
  });

export const deleteDepartment = (id) =>
  request(`/api/departments/${id}/`, { method: "DELETE" });

// ─── IMAP Config ───────────────────────────────────────────────────

export const getImapConfigs = () => request("/api/settings/imap/");

export const createImapConfig = (data) =>
  request("/api/settings/imap/", {
    method: "POST",
    body: JSON.stringify(data),
  });

export const updateImapConfig = (id, data) =>
  request(`/api/settings/imap/${id}/`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const testImapConnection = (data) =>
  request("/api/settings/imap/test/", {
    method: "POST",
    body: JSON.stringify(data),
  });

export const getDeptImapConfigs = () => request("/api/settings/department-imap/");

export const createDeptImapConfig = (data) =>
  request("/api/settings/department-imap/", {
    method: "POST",
    body: JSON.stringify(data),
  });

export const updateDeptImapConfig = (id, data) =>
  request(`/api/settings/department-imap/${id}/`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const deleteDeptImapConfig = (id) =>
  request(`/api/settings/department-imap/${id}/`, { method: "DELETE" });

// ─── Signature / Stamp ─────────────────────────────────────────────

export async function getStamp() {
  const token = localStorage.getItem("access");
  const response = await fetch(`${API_BASE_URL}/api/settings/signature/`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (response.status === 204) return null;
  if (!response.ok) throw new Error(`Error ${response.status}`);
  return response.json();
}

export async function uploadStamp(file) {
  const token = localStorage.getItem("access");
  const formData = new FormData();
  formData.append("stamp_image", file);
  const response = await fetch(`${API_BASE_URL}/api/settings/signature/`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail || data.error || `Error ${response.status}`);
  }
  return response.json();
}

export async function deleteStamp(id) {
  return request(`/api/settings/signature/${id}/`, { method: "DELETE" });
}

// ─── Meetings ──────────────────────────────────────────────────────

export const getMeetings = () => request("/api/meetings/");

export const createMeeting = (data) =>
  request("/api/meetings/", {
    method: "POST",
    body: JSON.stringify(data),
  });

export const acceptMeeting = (id) =>
  request(`/api/meetings/${id}/accept/`, { method: "POST" });

export const declineMeeting = (id) =>
  request(`/api/meetings/${id}/decline/`, { method: "POST" });

export const sendMeetingInvite = (id) =>
  request(`/api/meetings/${id}/send_invite/`, { method: "POST" });

// ─── Audit Logs ──────────────────────────────────────────────────

export const getAuditLogs = () => request("/api/audit-logs/");

// ─── Internal Messages ────────────────────────────────────────────

export const getInternalMessages = () => request("/api/internal-messages/");

export const createInternalMessage = (data) =>
  request("/api/internal-messages/", {
    method: "POST",
    body: JSON.stringify(data),
  });

export const generateInternalMessageText = (data) =>
  request("/api/internal-messages/generate_text/", {
    method: "POST",
    body: JSON.stringify(data),
  });

export const getUnreadInternalCount = () =>
  request("/api/internal-messages/unread_count/");

export const markInternalMessageRead = (id) =>
  request(`/api/internal-messages/${id}/mark_read/`, { method: "POST" });

export const acceptInternalMessage = (id) =>
  request(`/api/internal-messages/${id}/accept/`, { method: "POST" });

export const declineInternalMessage = (id) =>
  request(`/api/internal-messages/${id}/decline/`, { method: "POST" });

export const replyToInternalMessage = (id, body) =>
  request(`/api/internal-messages/${id}/reply/`, {
    method: "POST",
    body: JSON.stringify({ body }),
  });
